/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { projectsCreateProject } from "../funcs/projectsCreateProject.js";
import { projectsDeleteProject } from "../funcs/projectsDeleteProject.js";
import { projectsGetProject } from "../funcs/projectsGetProject.js";
import { projectsListProjects } from "../funcs/projectsListProjects.js";
import { projectsUpdateProject } from "../funcs/projectsUpdateProject.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import * as components from "../models/components/index.js";
import * as operations from "../models/operations/index.js";
import { unwrapAsync } from "../types/fp.js";
import { PageIterator, unwrapResultIterator } from "../types/operations.js";

export class Projects extends ClientSDK {
  /**
   * Create project
   *
   * @remarks
   * Create a project to group deployments and applications under a workspace-scoped slug.
   *
   * The slug you provide is the stable, caller-defined handle used to reference this project in subsequent operations (get, update, delete). It must be unique within your workspace.
   *
   * **Important**: The slug cannot collide with an existing project in your workspace. A duplicate slug returns a 409 conflict.
   *
   * **Required Permissions**
   *
   * Your root key must have the following permission:
   * - `project.*.create_project` (to create projects in your workspace)
   */
  async createProject(
    request: components.V1ProjectsCreateProjectRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1ProjectsCreateProjectResponseBody> {
    return unwrapAsync(projectsCreateProject(
      this,
      request,
      options,
    ));
  }

  /**
   * Delete project
   *
   * @remarks
   * Delete an existing project in your workspace, identified by its id.
   *
   * Deletion is asynchronous and eventually consistent. The project and all of its associated resources (apps, environments, deployments, custom domains) are torn down by a background workflow. A successful response indicates the deletion was enqueued, not that every resource has already been removed.
   *
   * Projects with delete protection enabled cannot be deleted until protection is disabled.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `project.*.delete_project` (to delete any project)
   * - `project.<project_id>.delete_project` (to delete a specific project)
   */
  async deleteProject(
    request: components.V1ProjectsDeleteProjectRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1ProjectsDeleteProjectResponseBody> {
    return unwrapAsync(projectsDeleteProject(
      this,
      request,
      options,
    ));
  }

  /**
   * Get project
   *
   * @remarks
   * Retrieve a single project in your workspace by its id.
   *
   * Use this to fetch project details after creation, verify a project exists before performing operations, or resolve a project's metadata from its id.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `project.*.read_project` (to read any project)
   * - `project.<project_id>.read_project` (to read a specific project)
   */
  async getProject(
    request: components.V1ProjectsGetProjectRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1ProjectsGetProjectResponseBody> {
    return unwrapAsync(projectsGetProject(
      this,
      request,
      options,
    ));
  }

  /**
   * List projects
   *
   * @remarks
   * Retrieve a paginated list of projects in your workspace.
   *
   * Use this to build project management dashboards or to enumerate projects for administrative purposes. Results are ordered by project id and returned in pages. When `hasMore` is true, pass the returned `cursor` to fetch the next page.
   *
   * **Required Permissions**
   *
   * Your root key must have the following permission:
   * - `project.*.read_project` (to read projects in your workspace)
   */
  async listProjects(
    request: components.V1ProjectsListProjectsRequestBody,
    options?: RequestOptions,
  ): Promise<
    PageIterator<operations.ProjectsListProjectsResponse, { cursor: string }>
  > {
    return unwrapResultIterator(projectsListProjects(
      this,
      request,
      options,
    ));
  }

  /**
   * Update project
   *
   * @remarks
   * Update an existing project in your workspace, identified by its id.
   *
   * The project name, slug, and delete protection setting can be changed. Omitted fields are left unchanged. Changing the slug affects the deployment domains generated for this project.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `project.*.update_project` (to update any project)
   * - `project.<project_id>.update_project` (to update a specific project)
   */
  async updateProject(
    request: components.V1ProjectsUpdateProjectRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1ProjectsUpdateProjectResponseBody> {
    return unwrapAsync(projectsUpdateProject(
      this,
      request,
      options,
    ));
  }
}
